import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './shadcn/card';
import { Skeleton } from './shadcn/skeleton';
import { CheckCircle, Users, Database, BarChart3, Clock, Zap, CreditCard, Upload, Mail } from 'lucide-react';
import { isLandingOnly } from '../utils/buildMode';
import apiClient from '../lib/api';

const PlanComparison = () => {
  const navigate = useNavigate();
  const [visibleElements, setVisibleElements] = useState([]);
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Trigger animations when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisibleElements(['tiers', 'features', 'button']);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Fetch pricing data from API
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true);
        // Fetch pricing data from the API using configured API client
        const response = await apiClient.get('/api/pricing');
        setPricingData(response.data);
      } catch (err) {
        setError(err.message);
        // Use default data if API fails
        setPricingData({
          tiers: [
            {
              hours: '0-50 hours',
              price: '$24 per hour',
              description: 'Perfect for small projects'
            },
            {
              hours: '51-100 hours',
              price: '$23 per hour',
              description: 'Best value for regular users'
            },
            {
              hours: '101+ hours',
              price: '$22 per hour',
              description: 'Maximum savings'
            }
          ],
          features: [
            { name: 'Video Processing', value: 'Pay per hour analyzed' },
            { name: 'Storage', value: 'Included with processing time' },
            { name: 'Basic Analytics', value: 'Standard reports' },
            { name: 'Advanced Analytics', value: 'Custom dashboards' },
            { name: 'Email Support', value: 'Business hours' }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  const handleGetStarted = () => {
    navigate(isLandingOnly ? '/contact-us' : '/register');
  };

  // Show error state but still display default data
  if (error) {
    console.error('Error fetching pricing data:', error);
  }

  // Use fetched data or default data
  const pricingTiers = pricingData?.tiers || [];
  const features = pricingData?.features || [];

  return (
    <div className="container mx-auto py-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Pay-As-You-Go Video Analysis
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {isLandingOnly
            ? 'Only pay for the video processing time you actually use. Contact our team for pricing details and to get started.'
            : 'Only pay for the video processing time you actually use. No subscriptions, no hidden fees.'}
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <Card className="text-center">
          <CardContent className="p-6">
            <Users className="h-10 w-10 text-blue-500 mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">Team Collaboration</h3>
            <p className="text-gray-600 text-sm">Work together seamlessly</p>
          </CardContent>
        </Card>
        
        <Card className="text-center">
          <CardContent className="p-6">
            <Database className="h-10 w-10 text-green-500 mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">Secure Storage</h3>
            <p className="text-gray-600 text-sm">Enterprise-grade security</p>
          </CardContent>
        </Card>
        
        <Card className="text-center">
          <CardContent className="p-6">
            <BarChart3 className="h-10 w-10 text-purple-500 mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">Advanced Analytics</h3>
            <p className="text-gray-600 text-sm">Detailed reporting</p>
          </CardContent>
        </Card>
      </div>

      {/* Pay-As-You-Go Pricing */}
      <Card className="mb-12">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Simple, Transparent Pricing</CardTitle>
          <CardDescription>
            Pay only for the video processing time you use
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pricing Tiers */}
            <div className={`transition-all duration-700 ${visibleElements.includes('tiers') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <h3 className="font-semibold text-lg mb-4 text-center">Volume Pricing</h3>
              <div className="space-y-4">
                {loading ? (
                  // Skeleton loading for pricing tiers
                  <div className="space-y-4">
                    {[...Array(3)].map((_, index) => (
                      <div key={index} className="flex justify-between items-center p-4 border rounded-lg bg-gray-50">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32 rounded" />
                          <Skeleton className="h-3 w-40 rounded" />
                        </div>
                        <Skeleton className="h-5 w-20 rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  pricingTiers.map((tier, index) => (
                    <div
                      key={index}
                      className={`flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 transition-all duration-300 transform hover:-translate-y-1 ${
                        visibleElements.includes('tiers') ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <div>
                        <span className="font-medium">{tier.hours}</span>
                        <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
                      </div>
                      <span className="font-bold text-lg">{tier.price}</span>
                    </div>
                  ))
                )}
              </div>

              <div className={`mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 transition-all duration-700 delay-300 ${
                visibleElements.includes('tiers') ? 'opacity-100' : 'opacity-0'
              }`}>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="font-medium">All features included in every tier</span>
                </div>
              </div>
            </div>
            
            {/* Features List */}
            <div className={`transition-all duration-700 ${visibleElements.includes('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <h3 className="font-semibold text-lg mb-4 text-center">All Features Included</h3>
              <div className="grid grid-cols-1 gap-3">
                {loading ? (
                  // Skeleton loading for features list
                  <div className="space-y-3">
                    {[...Array(5)].map((_, index) => (
                      <div key={index} className="flex items-center p-3 rounded">
                        <div className="p-1 rounded-full mr-3 bg-gray-200">
                          <div className="h-4 w-4 rounded-full bg-gray-300" />
                        </div>
                        <div className="space-y-1 flex-1">
                          <Skeleton className="h-4 w-32 rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  features.map((feature, index) => (
                    <div
                      key={index}
                      className={`flex items-center p-3 hover:bg-gray-50 rounded transition-all duration-300 ${
                        visibleElements.includes('features') ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{ transitionDelay: `${index * 50}ms` }}
                    >
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span>{feature.name}</span>
                    </div>
                  ))
                )}
              </div>

              <div className={`mt-6 transition-all duration-700 delay-500 ${
                visibleElements.includes('button') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}>
                <Button
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 w-full py-6 text-lg transform hover:-translate-y-1 transition duration-300"
                >
                  {isLandingOnly ? 'Contact Us for Pricing' : 'Get Started with Pay-Per-Hour'}
                  {isLandingOnly ? <Mail className="ml-2 h-5 w-5" /> : <CreditCard className="ml-2 h-5 w-5" />}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="mb-12">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">How It Works</CardTitle>
          <CardDescription>
            Simple process, maximum flexibility
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
            <div className={`text-center transition-all duration-700 ${visibleElements.includes('tiers') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transform hover:scale-110 transition-transform duration-300">
                <Upload className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">1. Upload Videos</h3>
              <p className="text-gray-600">
                Upload traffic videos to our secure platform. We'll automatically detect the video length.
              </p>
            </div>
            <div className={`text-center transition-all duration-700 delay-200 ${visibleElements.includes('tiers') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transform hover:scale-110 transition-transform duration-300">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">2. Processing Begins</h3>
              <p className="text-gray-600">
                Our AI analyzes your video and generates traffic movement counts. You're only charged for the processing time used.
              </p>
            </div>
            <div className={`text-center transition-all duration-700 delay-400 ${visibleElements.includes('tiers') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transform hover:scale-110 transition-transform duration-300">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">3. Pay Per Use</h3>
              <p className="text-gray-600">
                Only charged for the processing time used. No subscriptions, no hidden fees.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
        <CardContent className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">
            {isLandingOnly ? 'Interested in Pricing?' : 'Ready to Get Started?'}
          </h2>
          <p className="mb-6 max-w-xl mx-auto">
            {isLandingOnly
              ? 'Contact our team to discuss your project, get a tailored quote, and start processing videos.'
              : 'Join transportation professionals using our platform. No subscription required.'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            {isLandingOnly ? (
              <Button 
                size="lg" 
                variant="secondary"
                onClick={() => navigate('/contact-us')}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Contact Us for Pricing
              </Button>
            ) : (
              <Button 
                size="lg" 
                variant="secondary"
                onClick={handleGetStarted}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Create Account
              </Button>
            )}
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/contact')}
              className="text-white border-white hover:bg-white hover:text-blue-600"
            >
              Contact Sales
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanComparison;